import { Model } from 'pinia-orm';
import { Variation } from './Variation';
import { Page } from './Page';

export class Experiment extends Model {
  static entity = 'experiments';
  static primaryKey = 'id';

  static fields() {
    return {
      id: this.uid(),
      name: this.string(''),
      hypothesis: this.string(''), // Maps to 'description' from API
      flowType: this.string('single'), // Maps to flow type
      websiteId: this.string(''),
      status: this.string('draft'),
      trafficAllocation: this.number(0),
      isActive: this.boolean(false),
      createdAt: this.attr(null),
      updatedAt: this.attr(null),

      // Missing fields from original
      type: this.string('ab_test'), // 'ab_test' or 'personalization'
      edited_at: this.attr(null),
      validated: this.attr({
        general: null,
        targeting: null,
        variations: null,
        goals: null,
        start: null,
      }),
      multi_page: this.boolean(false),
      page_targeting: this.attr(null), // null or 'custom_urls'
      audience_targeting: this.attr(null), // null, 'custom_audience', or 'my_audiences'
      audience_id: this.attr(null),
      custom_variation_weights: this.attr(null),
      mutually_exclusive_group_id: this.attr(null),

      // Relationships
      variations: this.hasMany(Variation, 'experimentId'),
      pages: this.hasMany(Page, 'experimentId'),
      // TODO: Add these models when implemented
      // entry_urls: this.hasMany(UrlRule, 'experience_id'),
      // targeting_rules: this.hasMany(TargetingRule, 'experience_id'),
      // goals: this.hasMany(Goal, 'experience_id'),
      // actions: this.hasMany(Action, 'experience_id')
    };
  }

  declare id: string | null;
  declare name: string;
  declare hypothesis: string;
  declare flowType: string;
  declare websiteId: string;
  declare status: string;
  declare trafficAllocation: number;
  declare isActive: boolean;
  declare createdAt: string | null;
  declare updatedAt: string | null;

  // Missing field declarations
  declare type: string;
  declare edited_at: string | null;
  declare validated: {
    general: boolean | null;
    targeting: boolean | null;
    variations: boolean | null;
    goals: boolean | null;
    start: boolean | null;
  };
  declare multi_page: boolean;
  declare page_targeting: string | null;
  declare audience_targeting: string | null;
  declare audience_id: string | null;
  declare custom_variation_weights: any;
  declare mutually_exclusive_group_id: string | null;

  // Relationships
  declare variations: Variation[];
  declare pages: Page[];

  // Computed properties for form validation
  get isValid(): boolean {
    return this.name.trim().length > 0;
  }

  // Track original values for change detection
  private _originalValues: Record<string, any> = {};

  // Initialize original values when loading from API
  static hydrate(data: any) {
    const experiment = new this(data);
    experiment._setOriginalValues();
    return experiment;
  }

  // Set original values for change tracking
  _setOriginalValues() {
    this._originalValues = {
      name: this.name,
      hypothesis: this.hypothesis,
      flowType: this.flowType,
      websiteId: this.websiteId,
      status: this.status,
      trafficAllocation: this.trafficAllocation,
      isActive: this.isActive,
    };
  }

  // Check if experiment has been modified
  get hasChanges(): boolean {
    return Object.keys(this._originalValues).some(
      (key) => this._originalValues[key] !== (this as any)[key]
    );
  }

  // Transform API response to model format
  static fromAPI(apiData: any): Experiment {
    return this.hydrate({
      id: apiData.id,
      name: apiData.name || '',
      hypothesis: apiData.description || '', // Map description to hypothesis
      flowType: apiData.flowType || 'single',
      websiteId: apiData.websiteId || '',
      status: apiData.status || 'draft',
      trafficAllocation: apiData.trafficAllocation || 0,
      isActive: apiData.isActive || false,
      createdAt: apiData.createdAt,
      updatedAt: apiData.updatedAt,

      // Additional fields
      type: apiData.type || 'ab_test',
      edited_at: apiData.edited_at,
      validated: apiData.validated || false,
      multi_page: apiData.multi_page || false,
      page_targeting: apiData.page_targeting || null,
      audience_targeting: apiData.audience_targeting || null,
      audience_id: apiData.audience_id || null,
      custom_variation_weights: apiData.custom_variation_weights || null,
      mutually_exclusive_group_id: apiData.mutually_exclusive_group_id || null,
    });
  }

  // Transform model to API format
  toAPI(): any {
    return {
      id: this.id,
      name: this.name,
      description: this.hypothesis, // Map hypothesis to description
      flowType: this.flowType,
      websiteId: this.websiteId,
      status: this.status,
      trafficAllocation: this.trafficAllocation,
      isActive: this.isActive,

      // Additional fields
      type: this.type,
      edited_at: this.edited_at,
      validated: this.validated,
      multi_page: this.multi_page,
      page_targeting: this.page_targeting,
      audience_targeting: this.audience_targeting,
      audience_id: this.audience_id,
      custom_variation_weights: this.custom_variation_weights,
      mutually_exclusive_group_id: this.mutually_exclusive_group_id,
    };
  }

  // Reset to original values
  reset() {
    Object.keys(this._originalValues).forEach((key) => {
      (this as any)[key] = this._originalValues[key];
    });
  }

  // Update original values after successful save
  updateOriginalValues() {
    this._setOriginalValues();
  }
}
